// Copyright ©️ 2026 eVolve MEP, LLC

import {
  formatDate,
  type DateOnly,
  type InsightsDetailsWorkOrder,
  type WorkCellTask,
  type WorkOrder,
  type WorkOrderPaginated,
  type ISOTimestamp,
} from '@evolvemep/api-ts';
import type { ColDef } from 'ag-grid-enterprise';

import { isNotNil, isNil } from 'helpers/isNotNil';
import {
  getDaysRemaining as getInstanceDaysRemaining,
  getDaysEarlyLate as getInstanceDaysEarlyLate,
} from 'modules/Shop/WorkOrders/WorkOrdersPage/common';

export const getNeedByDateColDef = <T,>({
  getNeedByDate,
  getIsLate,
}: {
  getNeedByDate: (data: T) => DateOnly | null | undefined;
  getIsLate: (data: T) => boolean;
}) =>
  ({
    colId: 'needByDate',
    headerName: 'Need By Date',
    width: 150,
    valueGetter: ({ data }) => (isNotNil(data) ? getNeedByDate(data) : undefined),
    valueFormatter: (p) => formatDate(p.value, 'MM/dd/yyyy', '--'),
    filter: 'agDateColumnFilter',
    filterParams: {
      filterOptions: ['inRange'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
    cellClassRules: {
      late: ({ data }) => isNotNil(data) && getIsLate(data),
    },
  }) as const satisfies ColDef<T>;

export const getOnSiteDateColDef = <T,>({ getOnSiteDate }: { getOnSiteDate: (data: T) => DateOnly | undefined }) =>
  ({
    colId: 'onSiteDate',
    headerName: 'On-site Date',
    width: 150,
    valueGetter: ({ data }) => (isNotNil(data) ? getOnSiteDate(data) : undefined),
    valueFormatter: (p) => formatDate(p.value, 'MM/dd/yyyy', '--'),
    filter: 'agDateColumnFilter',
    filterParams: {
      filterOptions: ['inRange'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
    cellClassRules: {
      late: ({ data }) => isNotNil(data) && getInstanceDaysRemaining(getOnSiteDate(data)) < 0,
    },
  }) as const satisfies ColDef<T>;

export const getDaysRemainingColDef = <T,>({
  getNeedByDate,
  getIsCompleted,
  getDaysRemaining,
}: {
  getNeedByDate: (data: T) => DateOnly | null | undefined;
  getIsCompleted: (data: T) => boolean;
  getDaysRemaining: (data: T) => number;
}) =>
  ({
    colId: 'days-remaining',
    headerName: 'Days Remaining',
    headerTooltip: 'The difference in days between the Current Date and Need By Date (overdue shown as negative)',
    width: 170,
    valueGetter: ({ data }) =>
      isNotNil(data)
        ? getIsCompleted(data) || isNil(getNeedByDate(data))
          ? ''
          : getDaysRemaining(data).toLocaleString()
        : '',
    valueFormatter: ({ value, data }) => value || (isNotNil(data) && getIsCompleted(data) ? '' : '--'),
    cellClassRules: {
      late: ({ data }) => isNotNil(data) && getDaysRemaining(data) < 0,
    },
    sortable: false,
  }) as const satisfies ColDef<T>;

export const getDaysEarlyDaysLateColDef = <T,>({
  hideDaysEarlyLate,
  getIsCompleted,
  getDaysEarlyLate,
}: {
  hideDaysEarlyLate?: boolean;
  getIsCompleted: (data: T) => boolean;
  getDaysEarlyLate: (data: T) => number;
}) =>
  ({
    colId: 'days-early-late',
    headerName: 'Days Early/Late',
    hide: hideDaysEarlyLate,
    headerTooltip: 'The difference in days between the Completed Date and Need By Date (late shown as negative)',
    width: 160,
    valueGetter: ({ data }) => (isNotNil(data) && getIsCompleted(data) ? getDaysEarlyLate(data) : ''),
    valueFormatter: ({ value }) => value ?? '',
    cellClassRules: {
      late: ({ data }) => isNotNil(data) && getDaysEarlyLate(data) < 0,
    },
    sortable: false,
  }) as const satisfies ColDef<T>;

export const getInsightsScheduleColDefs = () => {
  const getIsCompleted = (data: InsightsDetailsWorkOrder) => data.workOrderStateName === 'Completed';
  return {
    needByColDef: getNeedByDateColDef<InsightsDetailsWorkOrder>({
      getNeedByDate: (data) => data.needByDate,
      getIsLate: (data) => (getIsCompleted(data) ? data.daysLate > 0 : data.daysRemaining < 0),
    }),
    daysRemainingColDef: getDaysRemainingColDef<InsightsDetailsWorkOrder>({
      getNeedByDate: (data) => data.needByDate,
      getIsCompleted,
      getDaysRemaining: (data) => data.daysRemaining,
    }),
    daysEarlyLateColDef: getDaysEarlyDaysLateColDef<InsightsDetailsWorkOrder>({
      hideDaysEarlyLate: false,
      getIsCompleted,
      getDaysEarlyLate: (data) => (data.daysEarly ? data.daysEarly : data.daysLate * -1),
    }),
  };
};

export const getScheduleColDefs = <T extends WorkCellTask | WorkOrder | WorkOrderPaginated>({
  hideDaysEarlyLate,
  getIsCompleted,
}: {
  hideDaysEarlyLate?: boolean;
  getIsCompleted: (data: T) => boolean;
}) => {
  const getNeedByDate = (data: T) => data.needByDate;
  const getProductionCompletedOn = (data: T): ISOTimestamp | undefined =>
    'taskStatusTypeUpdatedOn' in data
      ? isNotNil(data.taskStatusTypeUpdatedOn)
        ? data.taskStatusTypeUpdatedOn
        : undefined
      : data.productionCompletedOn;
  const getDaysRemaining = (data: T) => getInstanceDaysRemaining(getNeedByDate(data));
  const getDaysEarlyLate = (data: T) => getInstanceDaysEarlyLate(getNeedByDate(data), getProductionCompletedOn(data));
  const getIsLate = (data: T) => (getIsCompleted(data) ? getDaysEarlyLate(data) < 0 : getDaysRemaining(data) < 0);
  return [
    getNeedByDateColDef({ getNeedByDate, getIsLate }),
    getDaysRemainingColDef({ getNeedByDate, getIsCompleted, getDaysRemaining }),
    getDaysEarlyDaysLateColDef({ hideDaysEarlyLate, getIsCompleted, getDaysEarlyLate }),
  ] as const;
};
